import { t } from 'i18next';
import { combineReducers } from 'redux';
import { call, put, putResolve, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { shouldDenyInOtherTabs } from '@o2/ui/O2Script';

import { environment } from 'environments/environment';

import { KAHL_AFTER_RELOAD_PROPERTY_KEY } from 'helpers/constants/generalConstants';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { getCurrentRoute } from 'helpers/routes/routes';
import { store } from 'helpers/store/configureStore';
import { AppState } from 'helpers/store/models/AppState';
import { history } from 'helpers/store/rootReducer';
import { GetUserInfoSuccessType } from 'models/AuthTypes';
import { LocaleTypeMap, UserPreferences } from 'models/context/UserPreferences';
import { adobeAnalyticsAfterPageLoadHandlerAction } from 'saga/adobeAnalytics/ducks';

import { AuthActionTypes, getUserInfoActions } from 'saga/auth/ducks';
import { validateContextCustomerAction } from 'saga/context/customer/ducks';
import { ContextActionTypes, validateContextAction } from 'saga/context/ducks';
import { ContextExternalActionTypes } from 'saga/context/external/ducks';
import { ContextInternalActionTypes } from 'saga/context/internal/ducks';
import { selectContextUserPreferencesInfo } from 'saga/context/userPreferences/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';

import { SharedObjectsActionTypes, SynchronizeContextAction } from 'saga/sharedObjects/ducks';
import { initI18NextActions, TranslationActionTypes } from 'saga/translation/ducks';

const IS_PROD_ENVIRONMENT_PROPERTY = 'gui.isProdEnvironment';

/* STATE */
export interface AppInitializationState {
  initialized: boolean;
  fromLFR: boolean;
}

/* ACTION TYPES */
export enum AppHandlersActionTypes {
  SetInitialized = '@@App/SET_INITIALIZED',
  AppRouteChanged = '@@App/ROUTE_CHANGED',
  SetFromLFR = '@@App/SET_FROM_LFR',
}

/* ACTIONS */
export const setInitializedAction = createActionCreator(AppHandlersActionTypes.SetInitialized);
export const appRouteChangedAction = createActionCreator(AppHandlersActionTypes.AppRouteChanged);
export const setFromLFRAction = createActionCreator(AppHandlersActionTypes.SetFromLFR);

/* REDUCERS */
const initialState: AppInitializationState = {
  initialized: false,
  fromLFR: false,
};

const fromLFR = createReducer(initialState.fromLFR, {
  [AppHandlersActionTypes.SetFromLFR]: (state: boolean, fromLFR: boolean) => fromLFR,
});

const initialized = createReducer(initialState.initialized, {
  [AppHandlersActionTypes.SetInitialized]: (state: boolean, initialized: boolean) => initialized,
});

export default combineReducers({
  initialized,
  fromLFR,
});

/* SELECTORS */
const selectInit = (state: AppState) => state.initialization;

export const isAppInitialized = (state: AppState) => selectInit(state).initialized;

export const isProdEnvironment = (): boolean => {
  return selectProperty(IS_PROD_ENVIRONMENT_PROPERTY)?.toLowerCase() === 'true';
};

export const isComingFromLFR = (state: AppState) => selectInit(state).fromLFR;

/**
 * Starting point for EA process:
 * Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Entities.Session (Context).validateContext.validateContext
 */
function* callValidateContext() {
  const urlParams = new URLSearchParams(window.location.search);

  const assetRefNo = urlParams.get('assetRefNo');
  const tsRefNo = urlParams.get('tsRefNo');
  const caRefNo = urlParams.get('caRefNo');
  const cuRefNo = urlParams.get('cuRefNo');

  const forceUpdate: boolean = yield select(isComingFromLFR);

  if (assetRefNo) {
    // validateContext: DIRECT_ASSET
    yield putResolve(
      validateContextAction.request({
        assetRefNo: assetRefNo,
        forceUpdate: forceUpdate,
      })
    );
  } else if (tsRefNo) {
    // validateContext: DIRECT_TS
    yield putResolve(
      validateContextAction.request({
        tsRefNo: tsRefNo,
        cleanContextFirst: true,
        forceUpdate: forceUpdate,
      })
    );
  } else if (caRefNo) {
    // validateContext: DIRECT_CA
    yield putResolve(
      validateContextAction.request({
        caRefNo: caRefNo,
        cleanContextFirst: true,
        forceUpdate: forceUpdate,
      })
    );
  } else if (cuRefNo) {
    // validateContext: DIRECT_CU
    yield putResolve(
      validateContextAction.request({
        cuRefNo: cuRefNo,
        cleanContextFirst: true,
      })
    );
  } else {
    yield putResolve(validateContextCustomerAction());
    yield putResolve(validateContextAction.success());
  }
}

function* callSynchronizeContext() {
  yield put(SynchronizeContextAction.request());
}

function* callAdobeAnalyticsAfterPageLoadHandler() {
  yield put(adobeAnalyticsAfterPageLoadHandlerAction());
}

function* finalizeAppInit() {
  // initialize localized texts
  const userPreferences: UserPreferences = yield select(selectContextUserPreferencesInfo);
  const locale = userPreferences?.locale
    ? Object.keys(LocaleTypeMap).find((key) => LocaleTypeMap[key] === userPreferences.locale)
    : environment.appTranslation.defaultLocale;

  yield put(initI18NextActions.request(locale));

  // all necessary actions are performed, the application is successfully initialized
  yield takeLeading(createActionType(TranslationActionTypes.InitI18Next, RequestActionTypes.SUCCESS), function* () {
    setPageTitle();
    setLastVisitedPageCookie(window.location.href);
    yield call(callAdobeAnalyticsAfterPageLoadHandler);
    yield put(setInitializedAction(true));
    shouldDenyInOtherTabs();
  });
}

function* appRouteChanged() {
  const appInitialized: boolean = yield select(isAppInitialized);

  if (!appInitialized) {
    // skip appRouteChanged: appInitSaga should do everything important in this case
    return;
  }

  yield putResolve(setFromLFRAction(false));
  setPageTitle();
  setLastVisitedPageCookie(environment.home.slice(0, -1) + window.location.pathname + window.location.search);
  yield call(callSynchronizeContext);

  yield takeLeading(
    createActionType(SharedObjectsActionTypes.SynchronizeContext, RequestActionTypes.SUCCESS),
    callAdobeAnalyticsAfterPageLoadHandler
  );
}

/**
 * Sets the application context at the application initialization<br>
 * Handles:
 * <ul>
 *   <li>Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Components.Page.requestHandler.synchronizeContext</li>
 *   <li>Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Components.Page.requestHandler.validateContext</li>
 * </ul>
 */
function* setApplicationContext({ payload }: AppAction<GetUserInfoSuccessType>) {
  const appInitialized: boolean = yield select(isAppInitialized);

  if (appInitialized) {
    return;
  }

  const userInfo = payload.userInfo;

  if (userInfo?.logged) {
    // synchronizeContext at application initialization
    if (userInfo.internal) {
      yield takeLatest(
        createActionType(ContextInternalActionTypes.SetContextInternalUser, RequestActionTypes.SUCCESS),
        callSynchronizeContext
      );
    } else {
      yield takeLatest(
        createActionType(ContextExternalActionTypes.SetContextExternalUser, RequestActionTypes.SUCCESS),
        callSynchronizeContext
      );
    }

    // start validateContext
    yield takeLatest(
      createActionType(SharedObjectsActionTypes.SynchronizeContext, RequestActionTypes.SUCCESS),
      callValidateContext
    );

    // finalize application initialization
    yield takeLatest(createActionType(ContextActionTypes.ValidateContext, RequestActionTypes.SUCCESS), finalizeAppInit);
    yield takeLatest(createActionType(ContextActionTypes.ValidateContext, RequestActionTypes.FAILURE), finalizeAppInit);
  } else {
    // finalize application initialization
    yield call(finalizeAppInit);
  }
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Components.Page.requestHandler.Overwrite browser history
 */
function* handleDeeplinkFromOneCRM(deeplinkBacklinkerUrl: string) {
  const originalEntryLocation = JSON.parse(JSON.stringify(window.location));
  const urlParams = new URLSearchParams(originalEntryLocation.search);
  const fromLFR = urlParams.get('fromLFR');
  const errorFlag = urlParams.get('error_flag');

  if (fromLFR === 'true') {
    history.replace(deeplinkBacklinkerUrl);
    // Push original location
    history.push({
      ...originalEntryLocation,
      search: urlParams.toString(),
    });

    if (errorFlag) {
      urlParams.delete('error_flag');
    }

    // Remove fromLFR from url and replace without reload and pushing to history
    urlParams.delete('fromLFR');
    history.replace({
      ...originalEntryLocation,
      search: urlParams.toString(),
    });
    yield putResolve(setFromLFRAction(true));

    if (errorFlag) {
      yield putResolve(getKahlActions.request({ infoMessageIds: errorFlag }));
    }
  }
}

/**
 * Encapsulates actions performed during application initialization.
 */
export function* appInitSaga() {
  yield put(setInitializedAction(false));

  selectProperty(IS_PROD_ENVIRONMENT_PROPERTY);

  const deeplinkBacklinkerUrl: string = selectProperty('gui.backlink.backlinker');

  yield call(handleDeeplinkFromOneCRM, deeplinkBacklinkerUrl);

  // Stop full initialization if pathname === deeplinkBacklinkerUrl. In case of leaving page
  if (window.location.pathname === deeplinkBacklinkerUrl) {
    yield put(setInitializedAction(true));

    return;
  }

  const reloadKahlValue = localStorage.getItem(KAHL_AFTER_RELOAD_PROPERTY_KEY);

  if (reloadKahlValue) {
    yield put(getKahlActions.request({ infoMessageIds: reloadKahlValue }));
    localStorage.removeItem(KAHL_AFTER_RELOAD_PROPERTY_KEY);
  }

  // register listener that triggers actions to be performed if the URL changes
  history.listen((location) => {
    if (location.pathname === deeplinkBacklinkerUrl) {
      /*
       * history.goBack() doesn't work correctly if mo2care and onecrm are in the same domain.
       * Browser just changes URL to /backlinker and react-router tries to render corresponding component = blank page is rendered
       * window.location.href should work better in this case
       */
      window.location.href = deeplinkBacklinkerUrl;
    } else {
      Promise.resolve().then(() => {
        store.dispatch({
          type: AppHandlersActionTypes.AppRouteChanged,
        });
      });
    }
  });

  yield put(getUserInfoActions.request(true));
  yield takeLeading(createActionType(AuthActionTypes.GetUserInfo, RequestActionTypes.SUCCESS), setApplicationContext);
}

/**
 * Encapsulates actions performed after the app routes have been changed.
 */
export function* appRouteChangedSaga() {
  yield takeLatest(createActionType(AppHandlersActionTypes.AppRouteChanged), appRouteChanged);
}

const setPageTitle = () => {
  const pageData = getCurrentRoute()?.commonPageData;

  if (pageData) {
    document.title = t(pageData.pageTitle) || pageData.pageTitle;
  }
};

/**
 * Set lastVisitedPage to cookies for backlinker
 */
const setLastVisitedPageCookie = (url: string) => {
  const domain = environment.buildProperties.backlinkCookieLastVisitedPageDomain
    ? `domain=${environment.buildProperties.backlinkCookieLastVisitedPageDomain};`
    : '';

  document.cookie = `mo2care-lastVisitedPage=${encodeURIComponent(
    url
  )}; ${domain} expires=none; path=/; maxAge=none; secure: false; httpOnly: false; sameSite: none;`;
};
